import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, FileText } from "lucide-react";
import { useTranslation } from "react-i18next";
import { OrbitalBackdrop, RotatingRing } from "@/components/brand/Sigils";
import { org, values, timeline } from "@/data/content";

export default function About() {
  const { t } = useTranslation();
  return (
    <div className="relative">
      <section className="relative pt-16 pb-20 overflow-hidden" data-testid="about-hero">
        <OrbitalBackdrop subtle />
        <div className="container-iyaf relative">
          <p className="eyebrow text-iyaf-blue mb-5">{t("about.eyebrow")}</p>
          <h1 className="font-display font-extrabold tracking-tighter leading-[0.95] text-5xl sm:text-6xl lg:text-7xl max-w-4xl text-balance">
            {t("about.title")}
          </h1>
          <p className="mt-8 font-editorial text-2xl sm:text-3xl text-iyaf-ink/75 leading-snug max-w-3xl">
            {t("about.lead")}
          </p>
          <div className="mt-10 flex flex-wrap gap-3">
            <Link to="/profile" data-testid="about-cta-profile" className="inline-flex items-center gap-2 px-7 py-4 rounded-full bg-iyaf-ink text-white font-semibold hover:bg-iyaf-blue transition-colors">
              <FileText size={18} /> {t("about.profileCta")}
            </Link>
            <Link to="/model" className="inline-flex items-center gap-2 px-7 py-4 rounded-full border-2 border-iyaf-ink font-semibold hover:bg-iyaf-ink hover:text-white transition-colors">
              {t("cta.seeHowTheModelWorks")} <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* Story */}
      <section className="py-20">
        <div className="container-iyaf grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-7 space-y-6 text-iyaf-ink/80 text-lg leading-relaxed">
            <p className="font-editorial text-2xl sm:text-3xl text-iyaf-ink leading-snug">
              {t("about.storyIntro")}
            </p>
            <p>{t("about.storyP1")}</p>
            <p>{t("about.storyP2")}</p>
            <p>{t("about.storyP3")}</p>
            <p>{t("about.storyP4")}</p>
          </div>

          <aside className="lg:col-span-5">
            <div className="rounded-3xl border border-black/5 bg-white p-8 sticky top-28">
              <p className="eyebrow text-iyaf-blue mb-4">{t("about.leadershipEyebrow")}</p>
              <ul className="space-y-4">
                {org.leadership.map((p) => (
                  <li key={p.name} className="border-b border-black/5 pb-4 last:border-0 last:pb-0">
                    <p className="font-display text-xl font-semibold">{p.name}</p>
                    <p className="text-sm text-iyaf-ink/60">{t(`about.leadershipRoles.${p.name === "Trad AlMasri" ? "trad" : "ameer"}`, p.role)}</p>
                  </li>
                ))}
              </ul>
              <div className="mt-6 pt-6 border-t border-black/5 grid grid-cols-3 gap-3 text-center">
                <div>
                  <p className="font-display text-2xl font-bold">{org.team.fullTime + org.team.partTime}</p>
                  <p className="text-[10px] uppercase tracking-widest text-iyaf-ink/50">{t("about.teamLabels.coreTeam")}</p>
                </div>
                <div>
                  <p className="font-display text-2xl font-bold">{org.team.volunteers}</p>
                  <p className="text-[10px] uppercase tracking-widest text-iyaf-ink/50">{t("about.teamLabels.volunteers")}</p>
                </div>
                <div>
                  <p className="font-display text-2xl font-bold">{org.team.mentors}</p>
                  <p className="text-[10px] uppercase tracking-widest text-iyaf-ink/50">{t("about.teamLabels.mentors")}</p>
                </div>
              </div>
            </div>
          </aside>
        </div>
      </section>

      {/* Mission / Vision */}
      <section className="py-20 bg-iyaf-ink text-white">
        <div className="container-iyaf grid lg:grid-cols-2 gap-10">
          <div>
            <p className="eyebrow text-iyaf-orange mb-4">{t("about.missionLabel")}</p>
            <p className="font-editorial text-3xl sm:text-4xl leading-snug">{t("about.missionBody")}</p>
          </div>
          <div>
            <p className="eyebrow text-iyaf-magenta mb-4">{t("about.visionLabel")}</p>
            <p className="font-editorial text-3xl sm:text-4xl leading-snug">{t("about.visionBody")}</p>
          </div>
        </div>
      </section>

      <section className="py-24">
        <div className="container-iyaf">
          <p className="eyebrow text-iyaf-blue mb-4">{t("about.valuesEyebrow")}</p>
          <h2 className="h2 max-w-3xl text-balance mb-12">{t("about.valuesTitle")}</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {values.map((v, i) => (
              <motion.div
                key={v.tKey}
                initial={{ opacity: 0, y: 14 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.5, delay: i * 0.04 }}
                className="p-7 rounded-3xl border border-black/5 bg-white hover:-translate-y-1 transition-transform"
                data-testid={`value-${v.tKey}`}
              >
                <p className="font-display text-3xl text-iyaf-blue mb-3">{String(i + 1).padStart(2, "0")}</p>
                <h3 className="font-display text-xl font-semibold mb-2">{t(`aboutDeep.values.${v.tKey}.name`)}</h3>
                <p className="text-iyaf-ink/65 text-sm leading-relaxed">{t(`aboutDeep.values.${v.tKey}.desc`)}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 bg-white border-y border-black/5 relative overflow-hidden">
        <RotatingRing size={360} className="absolute -right-20 top-32 opacity-20" />
        <div className="container-iyaf relative">
          <p className="eyebrow text-iyaf-blue mb-4">{t("about.timelineEyebrow")}</p>
          <h2 className="h2 mb-14">{t("about.timelineTitle")}</h2>
          <ol className="relative border-l-2 border-iyaf-blue/20 pl-8 space-y-12 max-w-3xl">
            {timeline.map((tl, i) => (
              <motion.li
                key={tl.year}
                initial={{ opacity: 0, x: -16 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.45, delay: i * 0.03 }}
                className="relative"
              >
                <span className="absolute -left-[42px] top-1 w-4 h-4 rounded-full bg-iyaf-blue ring-4 ring-iyaf-paper" />
                <p className="font-display text-3xl font-bold text-iyaf-blue">{tl.year}</p>
                <p className="font-display text-xl font-semibold mt-1">{t(`aboutDeep.timeline.${tl.tKey}.title`)}</p>
                <p className="text-iyaf-ink/70 mt-1.5 leading-relaxed">{t(`aboutDeep.timeline.${tl.tKey}.body`)}</p>
              </motion.li>
            ))}
          </ol>
        </div>
      </section>
    </div>
  );
}
